import { InMemoryCache, defaultDataIdFromObject, makeVar, ReactiveVar, StoreObject } from '@apollo/client';
import compareAgendaItems from '@tools/compareAgendaItems';
import { MeQuery, Query } from '@graphql/gql/graphql';
import { getCustomCacheKeyWithLocaleForFloorPlan } from './floorPlan';

export const apolloCacheLocaleVar: ReactiveVar<string> = makeVar<string>(null);

export const getCustomCacheKeyWithLocale = (object: Readonly<StoreObject>): string => {
  const _locale = apolloCacheLocaleVar();
  if (_locale) {
    return `${object.__typename}:${object.id}:${_locale}`;
  }
  return `${object.__typename}:${object.id}`;
};

const getCustomCacheKeyForAppointmentParticipant = (object: Readonly<StoreObject>): string => {
  return `${object.__typename}:${object.id}:${object.type}`;
};

const getCustomCacheKeyForVideoAttendees = (object: Readonly<StoreObject>): string => {
  return `${object.__typename}:attendees`;
};

const getCustomCacheKeyForQaQuestions = (object: Readonly<StoreObject>): string => {
  return `${object.__typename}:${object.sessionId}`;
};

const mergeData = <T>(existing, incoming, { readField }): T => {
  const existingItems = existing?.data || [];
  const incomingItems = incoming?.data || [];

  // Remove duplicates
  const merged = [];
  const itemsSet = new Set();
  for (const item of [...existingItems, ...incomingItems]) {
    const itemId = readField('id', item);
    if (!itemsSet.has(itemId)) {
      itemsSet.add(itemId);
      merged.push(item);
    }
  }
  return { ...incoming, data: merged };
};

export const createCache = (options = {}): InMemoryCache => {
  return new InMemoryCache({
    ...options,
    // TODO dataIdFromObject is deprecated, switch to typePolicies
    // https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
    dataIdFromObject: (object): ReturnType<typeof defaultDataIdFromObject> => {
      const type = object.__typename;
      switch (type) {
        case 'AttendeePoll':
          return `${type}:${object.pollId}`;
        case 'PollQuestion':
          return `${type}:${object.questionId}`;
        case 'PollQuestionChoice':
          return `${type}:${object.choiceId}`;
        case 'PollQuestionChoiceResult':
          return `${type}:${object.choiceId}`;
        case 'ExhibitorConsentAnswer':
          return `${type}:${object.attendeeId}`;
        case 'ExhibitorConsentAnswerResponse': {
          const answer = object.answer as { attendeeId: string };
          return `${type}:${answer.attendeeId}`;
        }
        default:
          return defaultDataIdFromObject(object); // fall back to default handling
      }
    },
    typePolicies: {
      // Creating custom cache keys for types which holds the override translated text data
      // this will help when attendee switches languages, will use the cache if present for any language already loaded
      // Ex for custom cache key: `Event:ed14ca79-04a6-4c7b-a119-8a9966dbac57:en-US`
      Event: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Attendee: {
        keyFields: getCustomCacheKeyWithLocale
      },
      AdmissionItem: {
        keyFields: getCustomCacheKeyWithLocale
      },
      QuestionConnection: {
        keyFields: getCustomCacheKeyForQaQuestions
      },
      Session: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SessionDetail: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SessionCategory: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Speaker: {
        keyFields: getCustomCacheKeyWithLocale
      },
      RegQuestionChoices: {
        keyFields: getCustomCacheKeyWithLocale
      },
      AgendaItem: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SponsoredSession: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Exhibitor: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Sponsor: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SessionExhibitor: {
        keyFields: getCustomCacheKeyWithLocale
      },
      CustomPageSettings: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideVideoLink: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideVideoGalleryCard: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideLinkCard: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideLinkGalleryCard: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideUrlLink: {
        keyFields: getCustomCacheKeyWithLocale
      },
      EventGuideVideoCard: {
        keyFields: getCustomCacheKeyWithLocale
      },
      PageGroup: {
        keyFields: getCustomCacheKeyWithLocale
      },
      FilterableCustomFields: {
        keyFields: getCustomCacheKeyWithLocale
      },
      FilterableCustomFieldsChoice: {
        keyFields: getCustomCacheKeyWithLocale
      },
      ExhibitorCategory: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SponsorshipLevel: {
        keyFields: getCustomCacheKeyWithLocale
      },
      ExhibitorDetails: {
        keyFields: getCustomCacheKeyWithLocale
      },
      HomeCard: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Credits: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Discussion: {
        keyFields: getCustomCacheKeyWithLocale
      },
      FloorPlan: {
        keyFields: getCustomCacheKeyWithLocaleForFloorPlan
      },
      VideoAttendee: {
        keyFields: ['attendeeId', 'meetingId']
      },
      VideoAttendeePaginatedResult: {
        keyFields: getCustomCacheKeyForVideoAttendees
      },
      GameInfo: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Game: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Goal: {
        keyFields: getCustomCacheKeyWithLocale
      },
      Achievement: {
        keyFields: getCustomCacheKeyWithLocale
      },
      ConflictingAgendaItems: {
        keyFields: getCustomCacheKeyWithLocale
      },
      SessionFilterValue: {
        keyFields: false
      },
      UpcomingCalendarEntry: {
        keyFields: getCustomCacheKeyWithLocale
      },
      PreFilter: {
        keyFields: false
      },
      PreFilterOption: {
        keyFields: false
      },
      OnDemandCatalog: {
        keyFields: false
      },
      OnDemandCatalogSection: {
        keyFields: false
      },
      AppointmentParticipant: {
        keyFields: getCustomCacheKeyForAppointmentParticipant
      },
      ReportPostsUnread: {
        keyFields: []
      },
      Query: {
        fields: {
          me: {
            merge(existing, incoming): MeQuery {
              return {
                ...existing,
                ...incoming,
                event: {
                  ...existing?.event,
                  ...incoming?.event
                }
              };
            }
          },
          getAttendeeHubConfig: {
            merge(existing, incoming): Query['getAttendeeHubConfig'] {
              return {
                ...existing,
                ...incoming
              };
            }
          },
          getSpeakerList: {
            keyArgs: ['criteria', ['eventId', 'featured', 'query', 'sort', 'locale']],
            merge(...args): Query['getSpeakerList'] {
              return mergeData<Query['getSpeakerList']>(...args);
            },
            read(existing, { variables: { criteria } }) {
              if (
                !existing ||
                (criteria?.pageCriteria.limit > existing.data?.length &&
                  existing.pagination?.totalCount > existing.data?.length)
              ) {
                // Returning undefined tells Apollo that this is a cache miss and we need to fetch data
                // We do this when there are not enough speakers in the cache but there are more to fetch.
                // Without this, Apollo thinks a previous cached result with 5 speakers can serve a query
                // that requests 12 speakers
                return undefined;
              }

              return existing;
            }
          },
          myAgenda: {
            /**
             * Avoid splitting queries by pageCriteria.pageToken so that
             * we can combine pagination results for /schedule page.
             */
            keyArgs: ['criteria', ['start', 'end', 'locale', 'term', 'agendaTypes']],

            // Merge existing with incoming, remove duplicates, sort by start, end, then name
            merge(existing, incoming, { readField }): Query['myAgenda'] {
              const existingItems = existing?.data || [];
              const incomingItems = incoming?.data || [];
              const existingTotalCount = existing?.pagination?.totalCount;

              // Remove duplicates
              const merged = [];
              const itemsSet = new Set();
              for (const item of [...existingItems, ...incomingItems]) {
                const itemId = readField('id', item);
                if (!itemsSet.has(itemId)) {
                  itemsSet.add(itemId);
                  merged.push(item);
                }
              }
              // Sort items
              merged.sort((a, b) => {
                const itemA = {
                  name: readField('name', a),
                  start: readField('start', a),
                  end: readField('end', a)
                };
                const itemB = {
                  name: readField('name', b),
                  start: readField('start', b),
                  end: readField('end', b)
                };
                return compareAgendaItems(itemA, itemB);
              });

              return {
                ...incoming,
                data: merged,
                pagination: {
                  ...incoming.pagination,
                  totalCount: existingTotalCount || incoming?.pagination?.totalCount
                }
              };
            }
          },
          searchAttendees: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: ['criteria', ['term', 'sort', 'filter', 'recommendations', 'attendeeIds']],

            merge(existing = { data: [] }, incoming = { data: [] }, { readField }): Query['searchAttendees'] {
              const merged = [...existing.data, ...incoming.data].reduce((map, row) => {
                const id = readField('id', row);
                map.set(id, row);
                return map;
              }, new Map());
              return { ...incoming, data: Array.from(merged.values()) };
            }
          },
          searchAttendeeConnections: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: ['criteria', ['status']],
            merge(
              existing = { data: [] },
              incoming = { data: [] },
              { readField, variables, toReference, cache }
            ): Query['searchAttendeeConnections'] {
              // Invoked when moving a pending connection to connected connection
              if (variables.newConnection) {
                return {
                  ...existing,
                  data: [
                    toReference(
                      cache.identify({
                        ...variables.newConnection,
                        __typename: 'AttendeeConnection'
                      })
                    ),
                    ...existing.data
                  ]
                };
              }
              // Invoked when declining (removing) a pending connection
              if (variables.removeConnection) {
                return {
                  ...existing,
                  data: existing.data.filter(
                    connection => readField('id', connection) !== variables.removeConnection.id
                  )
                };
              }

              // Merging the results of paginated data
              const merged = [...existing.data, ...incoming.data].reduce((map, row) => {
                const id = readField('id', row);
                map.set(id, row);
                return map;
              }, new Map());
              return { ...incoming, data: Array.from(merged.values()) };
            }
          },
          searchExhibitors: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: [
              'criteria',
              [
                'eventSponsor',
                'featured',
                'sortBySponsorshipLevel',
                'query',
                'sponsorshipLevelId',
                'hasVirtualBooth',
                'customFields',
                'excludeSponsorOnlyExhibitor',
                'categoryId',
                'prioritizeCategoryId'
              ],
              'locale'
            ],
            merge(...params): Query['searchExhibitors'] {
              return mergeData<Query['searchExhibitors']>(...params);
            }
          },
          getFloorPlan: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: ['eventId', 'floorPlanId', 'criteria', ['searchTerm', 'entityTypes']],
            merge(...params): Query['getFloorPlan'] {
              return mergeData<Query['getFloorPlan']>(...params);
            }
          },
          searchSponsors: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: ['criteria', [], 'locale'],
            merge(...params): Query['searchSponsors'] {
              return mergeData<Query['searchSponsors']>(...params);
            }
          },
          searchSessions: {
            // Cache key consists of all query variables, with the omission of paging criteria
            keyArgs: [
              'criteria',
              [
                'registrationTypeId',
                'admissionItemId',
                'status',
                'hasFee',
                'term',
                'featured',
                'recommended',
                'categories',
                'liveAndUpcoming',
                'timeframes',
                'sessionIds',
                'groupId',
                'includeUpgradeSessions',
                'locale',
                'customFieldFilters',
                'speakerIds',
                'tagIds',
                'locations'
              ]
            ],
            merge(...args): Query['searchSessions'] {
              return mergeData<Query['searchSessions']>(...args);
            }
          },
          upcomingEventsCalendar: {
            keyArgs: [
              'criteria',
              ['term', 'startTime', 'endTime', 'eventFormats', 'filters', 'locationFilter', 'locale']
            ],
            merge(existing, incoming, { readField }): Query['upcomingEventsCalendar'] {
              const existingItems = existing?.data || [];
              const incomingItems = incoming?.data || [];

              const merged = [];
              const itemsSet = new Set();
              for (const item of [...existingItems, ...incomingItems]) {
                const itemId = readField('id', item);
                if (!itemsSet.has(itemId)) {
                  itemsSet.add(itemId);
                  merged.push(item);
                }
              }
              return { ...incoming, data: merged };
            }
          }
        }
      }
    }
  });
};
